import type {
  AttributeCreate,
  AttributeType,
  AttributeUpdate,
  AttributeValidation,
} from '@messe/shared'
import { apiRequest } from './api.js'

/** One entry of the catalogue, as the API returns it. */
export interface AttributeView {
  id: string
  key: string
  label: string
  type: AttributeType
  system: boolean
  auto: boolean
  required: boolean
  active: boolean
  carryOver: boolean
  helpText: string | null
  group: string | null
  sortOrder: number
  showInList: boolean
  valueListId: string | null
  valueListKey: string | null
  validation: AttributeValidation
  storage: 'column' | 'jsonb' | 'relation'
  defaultValue: unknown
  createdAt: string
  updatedAt: string
}

export interface SchemaVersionView {
  id: string
  version: number
  status: 'draft' | 'published'
  notes: string | null
  fieldCount: number
  publishedAt: string | null
  publishedByName: string | null
  createdAt: string
}

export interface PublishResult {
  version: number
  id: string
  /** False when the catalogue was unchanged since the last version. */
  created: boolean
}

export function listAttributes(includeInactive = false): Promise<AttributeView[]> {
  const suffix = includeInactive ? '?includeInactive=true' : ''
  return apiRequest<AttributeView[]>(`/attributes${suffix}`)
}

export function createAttribute(input: AttributeCreate): Promise<AttributeView> {
  return apiRequest<AttributeView>('/attributes', { method: 'POST', body: input })
}

export function updateAttribute(
  id: string,
  changes: AttributeUpdate & { active?: boolean },
): Promise<AttributeView> {
  return apiRequest<AttributeView>(`/attributes/${id}`, { method: 'PATCH', body: changes })
}

/** Deactivates. Captured values keep their meaning; the field leaves the form. */
export async function deactivateAttribute(id: string): Promise<void> {
  // 204, so there is nothing to return.
  await apiRequest<undefined>(`/attributes/${id}`, { method: 'DELETE' })
}

export async function reorderAttributes(order: { id: string; sortOrder: number }[]): Promise<void> {
  await apiRequest<undefined>('/attributes/reorder', { method: 'POST', body: { order } })
}

export function listSchemaVersions(): Promise<SchemaVersionView[]> {
  return apiRequest<SchemaVersionView[]>('/attributes/versions')
}

export function publishSchema(notes?: string): Promise<PublishResult> {
  return apiRequest<PublishResult>('/attributes/publish', {
    method: 'POST',
    body: notes ? { notes } : {},
  })
}

/**
 * Whether the catalogue holds changes the devices have not seen.
 *
 * Editing a field changes nothing on any iPhone until the catalogue is
 * published — the app builds its form from a published version, not from the
 * live table. Without this the designer would look like it had taken effect,
 * and the field would silently be missing at the stand.
 *
 * Compares the newest change against the last publication rather than diffing
 * the snapshot: the server already refuses to create a version when nothing
 * changed, so this only has to decide what to *show*, and a wrong "unpublished
 * changes" hint costs a pointless click while a wrong "everything published"
 * costs a day of capture.
 */
export function hasUnpublishedChanges(
  attributes: AttributeView[],
  versions: SchemaVersionView[],
): boolean {
  // flatMap rather than filter-then-map: the filter narrows nothing for the
  // type checker, and asserting the result away would be asserting exactly the
  // thing this function exists to get right.
  const published = versions.flatMap((version) =>
    version.status === 'published' && version.publishedAt !== null
      ? [Date.parse(version.publishedAt)]
      : [],
  )

  if (published.length === 0) return attributes.length > 0

  const lastPublish = Math.max(...published)
  return attributes.some((attribute) => Date.parse(attribute.updatedAt) > lastPublish)
}

/** The published version currently in force, or null before the first publish. */
export function currentVersion(versions: SchemaVersionView[]): SchemaVersionView | null {
  const published = versions.filter((version) => version.status === 'published')
  if (published.length === 0) return null

  return published.reduce((newest, version) =>
    version.version > newest.version ? version : newest,
  )
}
